import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Typography,
  Checkbox,
  FormControlLabel,
  TextField,
  Box,
  Stack,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { deleteSupabaseInvoice } from "store/InvoiceSlice";

const CONFIRMATION_TEXT = "delete this invoice";

export default function DeleteInvoiceModal({ open, onClose, invoice }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [confirmChecks, setConfirmChecks] = useState({
    permanent: false,
    dataLoss: false,
    cannotUndo: false,
  });
  const [confirmWord, setConfirmWord] = useState("");

  const allAcknowledged = Object.values(confirmChecks).every(Boolean);
  const isConfirmValid =
    allAcknowledged && confirmWord.trim().toLowerCase() === CONFIRMATION_TEXT;

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!open) {
      setConfirmChecks({
        permanent: false,
        dataLoss: false,
        cannotUndo: false,
      });
      setConfirmWord("");
      setError(null);
    }
  }, [open]);

  const handleConfirm = async () => {
    if (!invoice?.id) {
      setError("Invoice ID is required for deletion.");
      return;
    }

    if (!isConfirmValid) {
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      const result = await dispatch(deleteSupabaseInvoice(invoice.id));

      if (deleteSupabaseInvoice.fulfilled.match(result)) {
        setProcessing(false);
        navigate(`/revenue`);
        onClose();
      } else {
        const errorMessage = result.payload || "Failed to delete invoice. Please try again.";
        setError(errorMessage);
        setProcessing(false);
      }
    } catch (err) {
      setError(err.message || "Failed to delete invoice. Please try again.");
      setProcessing(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="delete-invoice-dialog-title"
      aria-describedby="delete-invoice-dialog-description"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="delete-invoice-dialog-title">
        <Typography variant="h3">Delete Invoice?</Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          {invoice && (
            <Typography variant="body1" paragraph>
              This will delete the invoice <strong>'{invoice.name || invoice.invoice_num || "Unnamed Invoice"}'</strong>.
            </Typography>
          )}

          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {processing && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                p: 2,
                backgroundColor: "action.hover",
                borderRadius: 1,
              }}
            >
              <CircularProgress size={24} />
              <Typography>Deleting invoice...</Typography>
            </Box>
          )}

          <Typography variant="body2" fontWeight={600}>
            To delete this invoice, you must acknowledge each statement below.
          </Typography>
          <Stack spacing={1.5}>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 1.5,
                border: "1px solid",
                borderColor: confirmChecks.permanent
                  ? "primary.main"
                  : "divider",
                bgcolor: confirmChecks.permanent
                  ? "action.selected"
                  : "background.paper",
                transition: "all 0.2s ease",
              }}
            >
              <FormControlLabel
                sx={{
                  alignItems: "flex-start",
                  "& .MuiFormControlLabel-label": { mt: "2px" },
                }}
                control={
                  <Checkbox
                    checked={confirmChecks.permanent}
                    onChange={(event) =>
                      setConfirmChecks((prev) => ({
                        ...prev,
                        permanent: event.target.checked,
                      }))
                    }
                    disabled={processing}
                  />
                }
                label="I understand that this deletion is permanent. The invoice will be permanently removed and cannot be recovered."
              />
            </Box>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 1.5,
                border: "1px solid",
                borderColor: confirmChecks.dataLoss
                  ? "primary.main"
                  : "divider",
                bgcolor: confirmChecks.dataLoss
                  ? "action.selected"
                  : "background.paper",
                transition: "all 0.2s ease",
              }}
            >
              <FormControlLabel
                sx={{
                  alignItems: "flex-start",
                  "& .MuiFormControlLabel-label": { mt: "2px" },
                }}
                control={
                  <Checkbox
                    checked={confirmChecks.dataLoss}
                    onChange={(event) =>
                      setConfirmChecks((prev) => ({
                        ...prev,
                        dataLoss: event.target.checked,
                      }))
                    }
                    disabled={processing}
                  />
                }
                label="I understand that all invoice data, including totals, line items, and associated information will be permanently deleted."
              />
            </Box>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 1.5,
                border: "1px solid",
                borderColor: confirmChecks.cannotUndo
                  ? "primary.main"
                  : "divider",
                bgcolor: confirmChecks.cannotUndo
                  ? "action.selected"
                  : "background.paper",
                transition: "all 0.2s ease",
              }}
            >
              <FormControlLabel
                sx={{
                  alignItems: "flex-start",
                  "& .MuiFormControlLabel-label": { mt: "2px" },
                }}
                control={
                  <Checkbox
                    checked={confirmChecks.cannotUndo}
                    onChange={(event) =>
                      setConfirmChecks((prev) => ({
                        ...prev,
                        cannotUndo: event.target.checked,
                      }))
                    }
                    disabled={processing}
                  />
                }
                label="I understand that this action cannot be undone. If you need this invoice later, you should export it before deleting."
              />
            </Box>
          </Stack>
          <Box
            sx={{
              opacity: allAcknowledged ? 1 : 0,
              maxHeight: allAcknowledged ? 300 : 0,
              overflow: "hidden",
              transform: `translateY(${allAcknowledged ? 0 : -8}px)`,
              transition:
                "opacity 0.25s ease, max-height 0.3s ease, transform 0.25s ease",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              This action cannot be undone. Type{" "}
              <Typography component="span" fontWeight={600}>
                {CONFIRMATION_TEXT}
              </Typography>{" "}
              exactly to confirm.
            </Typography>
            <TextField
              sx={{ mt: 1.5 }}
              label="Confirmation phrase"
              placeholder={CONFIRMATION_TEXT}
              value={confirmWord}
              onChange={(event) => setConfirmWord(event.target.value)}
              fullWidth
              autoFocus={allAcknowledged}
              disabled={processing}
              error={confirmWord !== "" && confirmWord.trim().toLowerCase() !== CONFIRMATION_TEXT}
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" disabled={processing}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          color="error"
          variant="contained"
          disabled={!isConfirmValid || processing || !invoice?.id}
        >
          {processing ? "Deleting..." : "Delete Invoice"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}



